'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdMonitorHeart, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getVitalSign,
    createVitalSign,
    updateVitalSign,
    CreateVitalSignData,
    PainLevel,
    PAIN_LEVEL_LABELS,
} from '@/lib/api/hospital'
import { getAdmissions, Admission, ADMISSION_STATUS_LABELS } from '@/lib/api/hospital'

export default function VitalSignsFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [formData, setFormData] = useState<CreateVitalSignData>({
        admissionId: 0,
        temperature: undefined,
        heartRate: undefined,
        respiratoryRate: undefined,
        systolicBP: undefined,
        diastolicBP: undefined,
        oxygenSaturation: undefined,
        bloodGlucose: undefined,
        painLevel: undefined,
        consciousnessLevel: '',
        glasgowEyeResponse: undefined,
        glasgowVerbalResponse: undefined,
        glasgowMotorResponse: undefined,
        notes: '',
    })

    useEffect(() => {
        loadAdmissions()
        if (isEditing && id) {
            loadVitalSign(parseInt(id))
        }
    }, [id, isEditing])

    const loadAdmissions = async () => {
        try {
            const response = await getAdmissions(undefined, { status: 'ADM_ACTIVE' })
            if (response.success) {
                setAdmissions(response.result)
            }
        } catch (error) {
            console.error('Error loading admissions:', error)
        }
    }

    const loadVitalSign = async (vsId: number) => {
        setLoading(true)
        try {
            const response = await getVitalSign(vsId)
            if (response.success) {
                const vs = response.result
                setFormData({
                    admissionId: vs.admissionId,
                    temperature: vs.temperature,
                    heartRate: vs.heartRate,
                    respiratoryRate: vs.respiratoryRate,
                    systolicBP: vs.systolicBP,
                    diastolicBP: vs.diastolicBP,
                    oxygenSaturation: vs.oxygenSaturation,
                    bloodGlucose: vs.bloodGlucose,
                    painLevel: vs.painLevel,
                    consciousnessLevel: vs.consciousnessLevel || '',
                    glasgowEyeResponse: vs.glasgowEyeResponse,
                    glasgowVerbalResponse: vs.glasgowVerbalResponse,
                    glasgowMotorResponse: vs.glasgowMotorResponse,
                    notes: vs.notes || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar sinais vitais')
                navigate('/hospital/vital-signs')
            }
        } catch (error) {
            toast.error('Erro ao carregar sinais vitais')
            navigate('/hospital/vital-signs')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.admissionId) {
            toast.error('Selecione uma internação')
            return
        }

        setSaving(true)
        try {
            let response
            if (isEditing && id) {
                response = await updateVitalSign(parseInt(id), formData)
            } else {
                response = await createVitalSign(formData)
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Sinais vitais atualizados com sucesso'
                        : 'Sinais vitais registrados com sucesso'
                )
                navigate('/hospital/vital-signs')
            } else {
                toast.error(response.error || 'Erro ao salvar sinais vitais')
            }
        } catch (error) {
            toast.error('Erro ao salvar sinais vitais')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/vital-signs')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center shadow-lg">
                        <MdMonitorHeart className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Sinais Vitais' : 'Registrar Sinais Vitais'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os sinais vitais'
                                : 'Registre os sinais vitais do paciente'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Paciente</h3>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">Internação *</span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={formData.admissionId}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    admissionId: parseInt(e.target.value),
                                })
                            }
                            required
                        >
                            <option value={0}>Selecione uma internação</option>
                            {admissions.map(adm => (
                                <option key={adm.id} value={adm.id}>
                                    {adm.client?.name} - Leito {adm.bed?.number || 'S/N'}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Sinais Vitais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Temperatura (C)</span>
                            </label>
                            <input
                                type="number"
                                step="0.1"
                                className="input input-bordered w-full"
                                placeholder="36.5"
                                value={formData.temperature || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        temperature: e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Freq. Cardíaca (bpm)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="80"
                                value={formData.heartRate || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        heartRate: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Freq. Respiratória (rpm)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="16"
                                value={formData.respiratoryRate || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        respiratoryRate: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">PA Sistólica (mmHg)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="120"
                                value={formData.systolicBP || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        systolicBP: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">PA Diastólica (mmHg)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="80"
                                value={formData.diastolicBP || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        diastolicBP: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Saturação O2 (%)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="98"
                                value={formData.oxygenSaturation || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        oxygenSaturation: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Glicemia (mg/dL)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="100"
                                value={formData.bloodGlucose || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        bloodGlucose: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Nível de Dor (0-10)</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.painLevel ?? ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        painLevel: e.target.value
                                            ? (parseInt(e.target.value) as PainLevel)
                                            : undefined,
                                    })
                                }
                            >
                                <option value="">Selecione</option>
                                {Object.entries(PAIN_LEVEL_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>
                                        {key} - {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Escala de Glasgow</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Resposta Ocular (1-4)</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="4"
                                className="input input-bordered w-full"
                                value={formData.glasgowEyeResponse || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        glasgowEyeResponse: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Resposta Verbal (1-5)</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="5"
                                className="input input-bordered w-full"
                                value={formData.glasgowVerbalResponse || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        glasgowVerbalResponse: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Resposta Motora (1-6)</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="6"
                                className="input input-bordered w-full"
                                value={formData.glasgowMotorResponse || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        glasgowMotorResponse: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Observações</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered h-24 w-full"
                            placeholder="Observações adicionais..."
                            value={formData.notes}
                            onChange={e =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/vital-signs')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Registrar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
